import { useState, useRef, useEffect } from "react";
import { BRAND } from "../constants/brand";

const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL || "http://localhost:8000";

const WELCOME = {
  role: "bot",
  text: "Hi! I'm the Labarium AI assistant. Ask me anything about our laboratory services, tests, insurance, appointments, or how to get your results.",
};

const QUICK_QUESTIONS = [
  "What tests do you offer?",
  "How do I make an appointment?",
  "Which insurance do you accept?",
  "How do I get my results?",
];

export default function AIAssistantSection() {
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const threadRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const ask = async (override) => {
    const q = (override || input).trim();
    if (!q || loading) return;
    setMessages(prev => [...prev, { role: "user", text: q }]);
    setInput("");
    setLoading(true);

    const history = messages
      .filter(m => m !== WELCOME)
      .map(m => [m.role === "user" ? "human" : "ai", m.text]);

    try {
      const res = await fetch(`${CHAT_API_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q, history }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: "bot", text: data.answer || "I wasn't able to find an answer. Please call (877) 765-2274." }]);
    } catch {
      setMessages(prev => [...prev, { role: "bot", text: "I'm having trouble connecting right now. Please call (877) 765-2274 or email info@labarium.com." }]);
    }
    setLoading(false);
  };

  const handleKey = e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } };
  const hasConversation = messages.length > 1;

  return (
    <section className="rx-pad" style={{
      background: `linear-gradient(180deg, #000e24 0%, #00163a 100%)`,
      padding: "72px 40px 80px",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Subtle grid overlay */}
      <div style={{ position: "absolute", inset: 0, opacity: 0.02, pointerEvents: "none" }}>
        <svg width="100%" height="100%">
          <defs>
            <pattern id="ai-grid" width="48" height="48" patternUnits="userSpaceOnUse">
              <path d="M 48 0 L 0 0 0 48" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#ai-grid)"/>
        </svg>
      </div>

      {/* Ambient glow */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: 600, height: 300, borderRadius: "50%",
        background: "radial-gradient(ellipse, rgba(201,169,110,0.06) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />

      <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 1 }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <div style={{ width: 32, height: 1, background: BRAND.gold }} />
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: "0.32em", color: BRAND.gold, textTransform: "uppercase" }}>
              Labarium AI
            </span>
            <div style={{ width: 32, height: 1, background: BRAND.gold }} />
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(22px, 3vw, 34px)", color: BRAND.white, margin: 0, letterSpacing: "-0.01em" }}>
            Ask anything about Labarium
          </h2>
        </div>

        {/* ECG Waveform */}
        <div style={{
          position: "relative", height: 72, overflow: "hidden", marginBottom: 36,
          maskImage: "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)",
        }}>
          <div style={{ display: "flex", width: "200%", height: "100%", animation: `ecgScroll ${loading ? "3s" : "18s"} linear infinite`, transition: "animation-duration 0.5s ease" }}>
            {[0, 1].map(k => (
              <svg key={k} width="50%" height="100%" viewBox="0 0 800 72" preserveAspectRatio="none" style={{ flexShrink: 0 }}>
                <path d="M0,36 L60,36 L70,36 L80,10 L90,62 L100,36 L120,36 L130,36 L145,20 L155,52 L165,36 L190,36 L210,36 L225,4 L235,68 L245,36 L280,36 L300,36 L310,28 L318,44 L326,36 L360,36 L380,36 L390,36 L400,16 L410,56 L420,36 L450,36 L480,36 L490,36 L505,8 L515,64 L525,36 L560,36 L580,36 L590,36 L600,26 L608,46 L616,36 L650,36 L670,36 L685,36 L700,36 L714,22 L722,50 L730,36 L760,36 L800,36"
                  stroke={BRAND.gold} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
              </svg>
            ))}
          </div>
          <div style={{ position: "absolute", inset: 0, display: "flex", width: "200%", animation: `ecgScroll ${loading ? "3s" : "18s"} linear infinite`, animationDelay: "-9s" }}>
            {[0, 1].map(k => (
              <svg key={k} width="50%" height="100%" viewBox="0 0 800 72" preserveAspectRatio="none" style={{ flexShrink: 0 }}>
                <path d="M0,36 L60,36 L70,36 L80,10 L90,62 L100,36 L120,36 L130,36 L145,20 L155,52 L165,36 L190,36 L210,36 L225,4 L235,68 L245,36 L280,36 L300,36 L310,28 L318,44 L326,36 L360,36 L380,36 L390,36 L400,16 L410,56 L420,36 L450,36 L480,36 L490,36 L505,8 L515,64 L525,36 L560,36 L580,36 L590,36 L600,26 L608,46 L616,36 L650,36 L670,36 L685,36 L700,36 L714,22 L722,50 L730,36 L760,36 L800,36"
                  stroke={BRAND.gold} strokeWidth="0.8" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.2" />
              </svg>
            ))}
          </div>
        </div>

        {/* Conversation thread */}
        {hasConversation && (
          <div ref={threadRef} style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.07)",
            borderTop: `2px solid ${BRAND.gold}`,
            borderRadius: 16,
            marginBottom: 16,
            maxHeight: 420,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            padding: "20px 24px",
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: "flex",
                justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                marginBottom: i < messages.length - 1 ? 20 : 0,
              }}>
                {msg.role === "bot" && (
                  <div style={{ position: "relative", flexShrink: 0, marginRight: 14, marginTop: 2 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: "50%",
                      background: "rgba(201,169,110,0.12)",
                      border: "2px solid rgba(201,169,110,0.45)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="rgba(201,169,110,0.8)" strokeWidth="1.5"/><path d="M8 12h8M12 8v8" stroke="rgba(201,169,110,0.8)" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    </div>
                    <div style={{
                      position: "absolute", bottom: 1, right: 1, width: 12, height: 12,
                      borderRadius: "50%", background: "#22c55e",
                      border: "2px solid rgba(0,22,58,0.9)",
                    }} />
                  </div>
                )}
                <div style={{
                  maxWidth: "75%",
                  padding: "13px 18px",
                  borderRadius: msg.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                  background: msg.role === "user"
                    ? `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldLight})`
                    : "rgba(255,255,255,0.06)",
                  border: msg.role === "user" ? "none" : "1px solid rgba(255,255,255,0.08)",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 16,
                  fontWeight: msg.role === "user" ? 500 : 400,
                  color: msg.role === "user" ? BRAND.navyDeep : "rgba(255,255,255,0.85)",
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                  boxShadow: msg.role === "user" ? "0 4px 16px rgba(201,169,110,0.2)" : "none",
                }}>
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start", gap: 14 }}>
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <div style={{
                    position: "absolute", bottom: 1, right: 1, width: 15, height: 15,
                    borderRadius: "50%", background: "#22c55e",
                    border: "2px solid rgba(0,22,58,0.9)",
                  }} />
                </div>
                <div style={{ padding: "11px 16px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "14px 14px 14px 4px", display: "flex", gap: 5, alignItems: "center" }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: "rgba(201,169,110,0.6)", animation: `dotBounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Input */}
        <div style={{
          display: "flex", alignItems: "center",
          background: "rgba(255,255,255,0.05)",
          backdropFilter: "blur(20px)",
          border: `1px solid rgba(201,169,110,0.3)`,
          borderRadius: 16,
          padding: "6px 6px 6px 24px",
          marginBottom: 16,
          boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
          transition: "border-color 0.25s, box-shadow 0.25s",
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(201,169,110,0.6)"; e.currentTarget.style.boxShadow = "0 8px 40px rgba(0,0,0,0.4), 0 0 0 3px rgba(201,169,110,0.08)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(201,169,110,0.3)"; e.currentTarget.style.boxShadow = "0 8px 40px rgba(0,0,0,0.3)"; }}
        >
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder={hasConversation ? "Ask a follow-up question…" : "How can I help you today?"}
            style={{
              flex: 1, background: "none", border: "none", outline: "none",
              fontFamily: "'Inter', sans-serif", fontSize: 16,
              color: BRAND.white, fontWeight: 400, caretColor: BRAND.gold,
            }}
          />
          <button
            onClick={() => ask()}
            disabled={loading || !input.trim()}
            style={{
              background: loading || !input.trim() ? "rgba(201,169,110,0.25)" : `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldLight})`,
              border: "none",
              color: loading || !input.trim() ? "rgba(201,169,110,0.6)" : BRAND.navyDeep,
              width: 48, height: 48, borderRadius: 12, cursor: loading || !input.trim() ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              transition: "all 0.25s ease",
              boxShadow: loading || !input.trim() ? "none" : "0 4px 16px rgba(201,169,110,0.35)",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3 9h12M11 5l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Quick questions — only show before first message */}
        {!hasConversation && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            {QUICK_QUESTIONS.map(q => (
              <button
                key={q}
                onClick={() => ask(q)}
                style={{
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
                  color: "rgba(255,255,255,0.55)", fontFamily: "'Inter', sans-serif",
                  fontSize: 14, fontWeight: 400, padding: "10px 18px", borderRadius: 20,
                  cursor: "pointer", transition: "all 0.2s ease", letterSpacing: "0.02em",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(201,169,110,0.1)"; e.currentTarget.style.borderColor = "rgba(201,169,110,0.35)"; e.currentTarget.style.color = BRAND.gold; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "rgba(255,255,255,0.55)"; }}
              >
                {q}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
